// Maps QuickBooks Online P&L report JSON into the BCC monthly data format
// QB account names -> our JSON keys

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Map QB account names to our income category keys
const INCOME_MAP = {
  'Commissions': 'commissions',
  'Hourly Fees': 'commissions',
  'Investment Management': 'investment_management',
  'Subscription Fees': 'subscription_fees',
  'Tax Preparation': 'tax_preparation',
  'Tax Plan': 'tax_planning',
  'Services Income': null, // Parent section, skip
  'Unapplied Cash Payment Income': null,
};

// Map QB account names to our COGS category keys
const COGS_MAP = {
  'Payroll Taxes': 'payroll_taxes_direct',
  'Payroll Wages': 'payroll_wages_direct',
  'Referral Fees': 'referral_fees',
  'Direct Payroll': null, // Parent section
};

// Map QB account names to our expense category keys
const EXPENSE_MAP = {
  'Advertising and Promotion': 'advertising_promotional',
  'Advertising/Promotional': 'advertising_promotional',
  'Bank Service Charges': 'bank_service_charges',
  'Business Licenses and Permits': 'business_licenses_and_permits',
  'Client Service Fees': 'client_service_fees',
  'Consultant': 'consultant',
  'Continuing Education': 'continuing_education',
  'Dues and Subscriptions': 'dues_and_subscriptions',
  'Entertainment (ND)': 'meals_and_entertainment',
  'Gifts': 'gifts',
  'Insurance Expense': 'insurance_expense',
  'M&A': 'mergers_and_acquisitions',
  'Meals and Entertainment': 'meals_and_entertainment',
  'Miscellaneous Expenses': 'miscellaneous_expenses',
  'Office Supplies': 'office_supplies',
  'Non-Employee Compensation': 'payroll_non_employee',
  'Postage and Delivery': 'postage_and_delivery',
  'Professional Fees': 'professional_fees',
  'Software Subscriptions': 'software_subscriptions',
  'Telephone Expense': 'telephone_expense',
  'Travel Expense': 'travel_expense',
  'Utilities': 'utilities',
  'Payroll': null, // Parent section for Non-Employee Compensation
  'Estimated Tax': null, // Skip
  'Penalty and Interest': null,
  'Property Tax': 'utilities',
  'Unapplied Cash Bill Payment Expense': null,
};

// Parse a dollar value from QB (handles empty strings, negatives)
function parseQBValue(val) {
  if (!val || val === '' || val === '0.00') return 0;
  return Math.round(parseFloat(val.replace(/,/g, '')) * 100) / 100;
}

// Extract month index from column title (e.g., "Jan 2026" -> 0)
function getMonthIndex(colTitle) {
  const monthAbbrevs = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 0; i < monthAbbrevs.length; i++) {
    if (colTitle.startsWith(monthAbbrevs[i])) return i;
  }
  return -1;
}

// Recursively extract data rows from QB report sections
// Only extracts leaf-level Data rows, not section summaries
function extractDataRows(rows, group) {
  const results = [];
  if (!rows || !rows.Row) return results;

  for (const row of rows.Row) {
    if (row.type === 'Data' && row.ColData) {
      // Leaf-level data row - extract it
      results.push({ colData: row.ColData, group });
    } else if (row.type === 'Section' && row.Rows) {
      // Nested section (e.g., "Payroll" containing "Non-Employee Compensation")
      // Recurse into children but skip the section header/summary
      results.push(...extractDataRows(row.Rows, group));
    }
  }
  return results;
}

// Create an empty month structure
function emptyMonth() {
  return {
    income: {
      commissions: 0, investment_management: 0, subscription_fees: 0,
      tax_preparation: 0, tax_planning: 0,
    },
    cogs: {
      payroll_taxes_direct: 0, payroll_wages_direct: 0, referral_fees: 0,
    },
    expenses: {
      advertising_promotional: 0, bank_service_charges: 0, business_licenses_and_permits: 0,
      client_service_fees: 0, consultant: 0, continuing_education: 0,
      dues_and_subscriptions: 0, gifts: 0, insurance_expense: 0,
      mergers_and_acquisitions: 0, meals_and_entertainment: 0, miscellaneous_expenses: 0,
      office_supplies: 0, payroll_non_employee: 0, postage_and_delivery: 0,
      professional_fees: 0, software_subscriptions: 0, telephone_expense: 0,
      travel_expense: 0, utilities: 0,
    },
  };
}

/**
 * Transform a QuickBooks P&L report into our monthly data format.
 * @param {Object} qbReport - raw QB ProfitAndLoss report JSON
 * @returns {Object} { months: { jan: {...}, feb: {...}, ... }, year, type, unmapped: [...] }
 */
export function mapQBProfitAndLoss(qbReport) {
  const result = {
    type: 'actual',
    months: {},
    unmapped: [], // Track any QB accounts we don't recognize
  };

  // Initialize all 12 months
  for (const mk of MONTH_KEYS) {
    result.months[mk] = emptyMonth();
  }

  // Determine which columns correspond to which months
  const columns = qbReport.Columns?.Column || [];
  const monthColumns = []; // { index, monthIdx }
  for (let i = 0; i < columns.length; i++) {
    const mi = getMonthIndex(columns[i].ColTitle || '');
    if (mi >= 0) monthColumns.push({ colIndex: i, monthIdx: mi });
  }

  // Extract the year from the report period
  const startPeriod = qbReport.Header?.StartPeriod || '';
  result.year = parseInt(startPeriod.substring(0, 4)) || 2026;

  // Process each top-level section (Income, COGS, Expenses)
  const topRows = qbReport.Rows?.Row || [];

  for (const section of topRows) {
    const group = section.group; // 'Income', 'COGS', 'Expenses', 'GrossProfit', etc.
    if (!group || group === 'GrossProfit' || group === 'NetOperatingIncome' || group === 'NetIncome') continue;

    // Determine which map and category to use
    let accountMap;
    let category;
    if (group === 'Income') { accountMap = INCOME_MAP; category = 'income'; }
    else if (group === 'COGS') { accountMap = COGS_MAP; category = 'cogs'; }
    else if (group === 'Expenses') { accountMap = EXPENSE_MAP; category = 'expenses'; }
    else continue;

    // Extract all data rows from this section
    const dataRows = extractDataRows(section.Rows, group);

    for (const { colData } of dataRows) {
      const accountName = colData[0]?.value || '';
      const mappedKey = accountMap[accountName];

      if (mappedKey === undefined) {
        // Unknown account - track it
        result.unmapped.push({ account: accountName, group });
        continue;
      }

      if (mappedKey === null) continue; // Explicitly skipped (parent sections, etc.)

      // Add values for each month column
      for (const { colIndex, monthIdx } of monthColumns) {
        const value = parseQBValue(colData[colIndex]?.value);
        if (value !== 0) {
          const mk = MONTH_KEYS[monthIdx];
          // Use += to handle multiple QB accounts mapping to same key
          // (e.g., "Advertising and Promotion" + "Advertising/Promotional")
          result.months[mk][category][mappedKey] += Math.round(value);
        }
      }
    }
  }

  return result;
}

// QB class name -> our division key. 'Not Specified' is intentionally
// excluded: it's unallocated overhead that lives only at the global level,
// not a real division. (Confirmed from the live QB by-class report.)
const QB_CLASS_TO_DIVISION = {
  'Wealth Management': 'bwm',
  'Tax Services': 'bts',
  'Insurance Agency': 'bia',
};

/**
 * Transform a QuickBooks P&L summarized BY CLASS into our per-division
 * format for a single period (typically one month). Columns are classes;
 * rows are accounts (same section/account structure as the monthly P&L, so
 * we reuse the same INCOME/COGS/EXPENSE account maps).
 * @param {Object} qbReport - QB ProfitAndLoss report with summarize_column_by=Classes
 * @returns {Object} { bwm:{income,cogs,expenses}, bts:{...}, bia:{...}, unmapped:[] }
 */
export function mapQBProfitAndLossByClass(qbReport) {
  const result = { bwm: emptyMonth(), bts: emptyMonth(), bia: emptyMonth(), unmapped: [] };

  const columns = qbReport?.Columns?.Column || [];
  const classColumns = []; // { colIndex, division }
  columns.forEach((col, i) => {
    const div = QB_CLASS_TO_DIVISION[(col.ColTitle || '').trim()];
    if (div) classColumns.push({ colIndex: i, division: div });
  });
  // No recognizable class columns => class tracking off / unexpected shape.
  if (classColumns.length === 0) return result;

  const topRows = qbReport.Rows?.Row || [];
  for (const section of topRows) {
    const group = section.group;
    let accountMap, category;
    if (group === 'Income') { accountMap = INCOME_MAP; category = 'income'; }
    else if (group === 'COGS') { accountMap = COGS_MAP; category = 'cogs'; }
    else if (group === 'Expenses') { accountMap = EXPENSE_MAP; category = 'expenses'; }
    else continue;

    const dataRows = extractDataRows(section.Rows, group);
    for (const { colData } of dataRows) {
      const accountName = colData[0]?.value || '';
      const mappedKey = accountMap[accountName];
      if (mappedKey === undefined) { result.unmapped.push({ account: accountName, group }); continue; }
      if (mappedKey === null) continue; // parent section / skipped
      for (const { colIndex, division } of classColumns) {
        const value = parseQBValue(colData[colIndex]?.value);
        if (value !== 0) result[division][category][mappedKey] += Math.round(value);
      }
    }
  }
  return result;
}

/**
 * Transform a QuickBooks Cash Flow Statement into our format.
 * @param {Object} qbReport - raw QB CashFlow report JSON
 * @returns {Object} { months: [{ operatingCF, investingCF, distributions, otherFinancing }], bankBalance }
 */
export function mapQBCashFlow(qbReport) {
  const columns = qbReport.Columns?.Column || [];
  const monthColumns = [];
  for (let i = 0; i < columns.length; i++) {
    const mi = getMonthIndex(columns[i].ColTitle || '');
    if (mi >= 0) monthColumns.push({ colIndex: i, monthIdx: mi });
  }

  const result = { months: new Array(12).fill(null).map(() => ({
    operatingCF: 0, investingCF: 0, distributions: 0, otherFinancing: 0,
  }))};

  const topRows = qbReport.Rows?.Row || [];

  for (const section of topRows) {
    const summaryVals = section.Summary?.ColData || [];
    const sectionName = summaryVals[0]?.value || '';

    for (const { colIndex, monthIdx } of monthColumns) {
      const val = parseQBValue(summaryVals[colIndex]?.value);

      if (sectionName.includes('operating')) {
        result.months[monthIdx].operatingCF = Math.round(val);
      } else if (sectionName.includes('investing')) {
        result.months[monthIdx].investingCF = Math.round(val);
      } else if (sectionName.includes('financing')) {
        // Break financing into distributions and other
        const finRows = [];
        if (section.Rows?.Row) {
          for (const row of section.Rows.Row) {
            if (row.type === 'Data' && row.ColData) {
              const name = row.ColData[0]?.value || '';
              const amount = parseQBValue(row.ColData[colIndex]?.value);
              if (name.includes('Distribution') || name.includes('Owner')) {
                result.months[monthIdx].distributions += Math.round(amount);
              } else {
                result.months[monthIdx].otherFinancing += Math.round(amount);
              }
            }
          }
        }
      }
    }
  }

  return result;
}

/**
 * Extract bank balance from QB Balance Sheet.
 * @param {Object} qbReport - raw QB BalanceSheet report JSON
 * @returns {Object} { totalBankBalance, totalAssets }
 */
export function mapQBBalanceSheet(qbReport) {
  let totalBankBalance = 0;
  let totalAssets = 0;

  function findValue(rows, targetName) {
    if (!rows?.Row) return 0;
    for (const row of rows.Row) {
      if (row.Summary) {
        const name = row.Summary.ColData?.[0]?.value || '';
        if (name.includes(targetName)) {
          // Last column is the total
          const vals = row.Summary.ColData;
          return parseQBValue(vals[vals.length - 1]?.value);
        }
      }
      if (row.Rows) {
        const found = findValue(row.Rows, targetName);
        if (found !== 0) return found;
      }
      // Check Data rows too
      if (row.type === 'Data' && row.ColData) {
        const name = row.ColData[0]?.value || '';
        if (name.includes(targetName)) {
          const vals = row.ColData;
          return parseQBValue(vals[vals.length - 1]?.value);
        }
      }
    }
    return 0;
  }

  const topRows = qbReport.Rows || {};
  totalBankBalance = findValue(topRows, 'Total Bank Accounts');
  totalAssets = findValue(topRows, 'TOTAL ASSETS');

  return { totalBankBalance: Math.round(totalBankBalance), totalAssets: Math.round(totalAssets) };
}

/**
 * Merge QB actuals into existing P&L data.
 * QB data overwrites actuals months; projected months remain from the existing data.
 * @param {Object} existingData - current P&L data { months: {...} }
 * @param {Object} qbData - mapped QB data from mapQBProfitAndLoss()
 * @param {number} actualsCutoff - number of months that are actuals (e.g., 3 = Jan-Mar)
 * @returns {Object} merged data
 */
export function mergeQBIntoExisting(existingData, qbData, actualsCutoff) {
  const merged = JSON.parse(JSON.stringify(existingData));

  // Overwrite actuals months with QB data
  for (let i = 0; i < actualsCutoff; i++) {
    const mk = MONTH_KEYS[i];
    if (qbData.months[mk]) {
      merged.months[mk] = JSON.parse(JSON.stringify(qbData.months[mk]));
    }
  }

  return merged;
}
